/*
 * YourXI Bespoke XI: offers (gift card + squad copies)
 * --------------------------------------------------------------------------
 * window.YXIOffers
 *
 *   mountGiftCardPrompt(el, opts?)   -> {open(), close(), destroy()}
 *       A small link: "Buying for someone who'd rather pick their own XI? Give a YourXI gift card".
 *       Tapping it opens a sheet (bottom sheet on phones, dialog on desktop) with the three steps,
 *       a size choice and a "Buy a gift card" button.
 *       opts = { url, prices: {"12x16": "£..", "18x24": ".."}, variants: {"12x16": id, "18x24": id} }
 *       No url (gift card not live yet) -> the button reads "Gift cards coming soon" and is disabled.
 *
 *   mountGiftCardNote(el)            -> {destroy()}
 *       "Have a gift card? Build your XI, then apply it at checkout."
 *
 *   mountSquadCopies(el, {value, onChange(qty), unitPrice, tiers, max}) -> {update(qty), setUnitPrice(p), getValue(), destroy()}
 *       Own-team mode only. A copies stepper with quick picks, the tier table and a clear total.
 *       unitPrice: number in pounds (e.g. 89) or null for the placeholder "£XXX.XX".
 *       prices: {first, extra} in pounds. When given, the first copy costs "first" and each extra copy "extra"
 *       (no tier table, no % off). setPrices({first, extra}) updates it when the size changes.
 *       tiers: [{min, pct}] ascending. pct null shows "XX%" until it is set.
 *       The tier rule is the one Shopify's automatic discount applies: once the line reaches a tier,
 *       that % comes off EVERY copy (not only the extra ones). See OFFERS.md.
 *       The current value is mirrored to YXIOffers.squadQty so the cart adapter can read it.
 *
 *   squadPrice(qty, unitPrice, tiers) -> {qty, tier, pct, pctKnown, each, subtotal, saving, total, next}
 *   CONFIG  (TIERS, GIFT_URL, MAX_COPIES)
 *
 * Cart: one line, quantity N, the same line item properties. Shopify keeps every property on that
 * one line (tested 14 Oct on the hidden theme: /cart/add.js quantity 6, then /cart/change.js to 3).
 */
(function (root) {
  'use strict';

  var CONFIG = {
    // Placeholder tiers. Set the numbers, then the same numbers go in the Shopify discounts.
    TIERS: [{ min: 1, pct: 0 }, { min: 2, pct: null }, { min: 6, pct: null }],
    GIFT_URL: '/products/yourxi-gift-card',   // on sale since 15 Oct 2026 (fixed amounts from £25)
    MAX_COPIES: 40,
    QUICK: [1, 6, 11, 16],
    PRICE_PLACEHOLDER: '£XXX.XX',
    PCT_PLACEHOLDER: 'XX%'
  };

  var API = { CONFIG: CONFIG, squadQty: 1 };

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(n) {
    if (n == null || isNaN(n)) return CONFIG.PRICE_PLACEHOLDER;
    var v = Math.round(n * 100) / 100;
    var whole = v === Math.round(v);
    return '£' + v.toLocaleString('en-GB', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
  }
  function pctText(p) { return p == null ? CONFIG.PCT_PLACEHOLDER : (p + '%'); }
  function nb(s) { return s.replace(/ ([^ ]+)$/, ' $1'); } // no widows: glue the last word
  function clampQty(q, max) { q = parseInt(q, 10); if (isNaN(q) || q < 1) q = 1; return Math.min(q, max || CONFIG.MAX_COPIES); }

  /* ---------- pricing ---------- */
  function tierFor(qty, tiers) {
    var t = tiers[0];
    for (var i = 0; i < tiers.length; i++) if (qty >= tiers[i].min) t = tiers[i];
    return t;
  }
  function squadPrice(qty, unitPrice, tiers) {
    tiers = tiers || CONFIG.TIERS;
    qty = clampQty(qty, 1e6);
    var tier = tierFor(qty, tiers), idx = tiers.indexOf(tier);
    var pct = tier.pct, pctKnown = pct != null;
    var unitKnown = unitPrice != null && !isNaN(unitPrice);
    var subtotal = unitKnown ? unitPrice * qty : null;
    var each = unitKnown && pctKnown ? Math.round(unitPrice * (100 - pct)) / 100 : null;
    var total = each != null ? Math.round(each * qty * 100) / 100 : (pct === 0 ? subtotal : null);
    var saving = subtotal != null && total != null ? Math.round((subtotal - total) * 100) / 100 : null;
    var nextTier = tiers[idx + 1] || null;
    return {
      qty: qty, tier: tier, tierIndex: idx, pct: pct, pctKnown: pctKnown, unitKnown: unitKnown,
      each: pct === 0 ? (unitKnown ? unitPrice : null) : each, subtotal: subtotal, saving: saving, total: total,
      next: nextTier ? { tier: nextTier, more: nextTier.min - qty } : null
    };
  }

  function tierLabel(t, i, tiers) {
    var nx = tiers[i + 1];
    if (t.min === 1 && (!nx || nx.min === 2)) return '1 copy';
    if (!nx) return t.min + ' or more';
    if (nx.min - 1 === t.min) return t.min + ' copies';
    return t.min + ' to ' + (nx.min - 1);
  }

  /* ---------- icons ---------- */
  var ICON_GIFT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="square" aria-hidden="true"><rect x="3.5" y="9" width="17" height="11.5"/><path d="M2.5 9h19M12 9v11.5"/><path d="M12 9c-1.6-3.6-5.6-4.3-5.6-1.6C6.4 9 12 9 12 9Zm0 0c1.6-3.6 5.6-4.3 5.6-1.6C17.6 9 12 9 12 9Z"/></svg>';
  var ICON_CLOSE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19"/></svg>';

  /* ======================================================================
     Gift card: link + sheet
     ====================================================================== */
  var sheetCount = 0;
  function mountGiftCardPrompt(el, opts) {
    opts = opts || {};
    var url = opts.url !== undefined ? opts.url : CONFIG.GIFT_URL;
    var prices = opts.prices || {};
    var variants = opts.variants || {};
    var size = '12x16';
    var id = 'yxo-gift-' + (++sheetCount);
    var lastFocus = null;

    el.classList.add('yxo');
    el.innerHTML =
      '<button type="button" class="yxo-giftlink" aria-haspopup="dialog" aria-controls="' + id + '">' +
        '<span class="yxo-giftlink-icon">' + ICON_GIFT + '</span>' +
        '<span class="yxo-giftlink-text">' + esc(nb("Buying for someone who'd rather pick their own XI?")) +
        ' <u>' + esc(nb('Give a YourXI gift card')) + '</u></span>' +
      '</button>';

    var sheet = document.createElement('div');
    sheet.className = 'yxo yxo-sheet';
    sheet.id = id;
    sheet.hidden = true;
    sheet.innerHTML =
      '<div class="yxo-scrim" data-close></div>' +
      '<div class="yxo-panel" role="dialog" aria-modal="true" aria-labelledby="' + id + '-t">' +
        '<div class="yxo-panel-head">' +
          '<h2 class="yxo-h" id="' + id + '-t">Let them build it</h2>' +
          '<button type="button" class="yxo-x" data-close aria-label="Close">' + ICON_CLOSE + '</button>' +
        '</div>' +
        '<p class="yxo-lead">' + esc(nb('Not sure who they would pick? Give them the card and let them choose every player.')) + '</p>' +
        '<ol class="yxo-steps">' +
          '<li><b>Buy it now.</b><span>' + esc(nb('It arrives by email with a code and never expires.')) + '</span></li>' +
          '<li><b>They build their XI later.</b><span>' + esc(nb('Formation, players, kits and a message, on yourxi.co.uk, whenever they like.')) + '</span></li>' +
          '<li><b>We design it and frame it.</b><span>' + esc(nb('We email them a proof first, then print, frame and post it.')) + '</span></li>' +
        '</ol>' +
        '<span class="yxo-label">Gift card value</span>' +
        '<div class="yxo-opts" role="radiogroup" aria-label="Gift card value">' +
          [['12x16', '12 x 16 in framed XI'], ['18x24', '18 x 24 in framed XI']].map(function (o) {
            return '<button type="button" class="yxo-opt" role="radio" data-size="' + o[0] + '" aria-checked="' + (o[0] === size) + '">' +
              '<b>' + esc(o[1]) + '</b><small>' + esc(prices[o[0]] || CONFIG.PRICE_PLACEHOLDER) + '</small></button>';
          }).join('') +
        '</div>' +
        '<p class="yxo-fine">' + esc(nb('Or choose another amount on the gift card page. Cards start from £25.')) + '</p>' +
        (url
          ? '<a class="yxo-btn" data-buy href="#">Buy a gift card</a>'
          : '<button type="button" class="yxo-btn" disabled>Gift cards coming soon</button>') +
        '<p class="yxo-fine">' + esc(nb("It covers the print, frame and delivery up to its value. Any balance stays on the card.")) + '</p>' +
      '</div>';
    document.body.appendChild(sheet);

    function buyHref() {
      if (!url) return '#';
      var v = variants[size];
      return url + (v ? (url.indexOf('?') >= 0 ? '&' : '?') + 'variant=' + encodeURIComponent(v) : '');
    }
    function syncOpts() {
      sheet.querySelectorAll('.yxo-opt').forEach(function (b) { b.setAttribute('aria-checked', String(b.dataset.size === size)); });
      var a = sheet.querySelector('[data-buy]'); if (a) a.setAttribute('href', buyHref());
    }
    syncOpts();

    function open() {
      lastFocus = document.activeElement;
      sheet.hidden = false;
      document.documentElement.classList.add('yxo-lock');
      requestAnimationFrame(function () { sheet.classList.add('is-open'); });
      var f = sheet.querySelector('.yxo-opt[aria-checked="true"]'); if (f) f.focus({ preventScroll: true });
      document.addEventListener('keydown', onKey);
    }
    function close() {
      sheet.classList.remove('is-open');
      document.documentElement.classList.remove('yxo-lock');
      document.removeEventListener('keydown', onKey);
      setTimeout(function () { sheet.hidden = true; }, 220);
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }
    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'Tab') { // keep focus inside the sheet
        var f = sheet.querySelectorAll('button:not([disabled]),a[href]');
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    function onSheetClick(e) {
      if (e.target.closest('[data-close]')) { close(); return; }
      var o = e.target.closest('.yxo-opt');
      if (o) { size = o.dataset.size; syncOpts(); }
    }
    var trigger = el.querySelector('.yxo-giftlink');
    trigger.addEventListener('click', open);
    sheet.addEventListener('click', onSheetClick);

    return {
      open: open, close: close,
      destroy: function () { close(); sheet.remove(); el.innerHTML = ''; el.classList.remove('yxo'); }
    };
  }

  function mountGiftCardNote(el) {
    el.classList.add('yxo');
    el.innerHTML = '<p class="yxo-note"><span class="yxo-note-icon">' + ICON_GIFT + '</span><span>' +
      esc(nb('Have a gift card? Build your XI, then apply it at checkout.')) + '</span></p>';
    return { destroy: function () { el.innerHTML = ''; el.classList.remove('yxo'); } };
  }

  /* ======================================================================
     Squad copies (own-team mode)
     ====================================================================== */
  function mountSquadCopies(el, opts) {
    opts = opts || {};
    var tiers = opts.tiers || CONFIG.TIERS;
    var max = opts.max || CONFIG.MAX_COPIES;
    var unitPrice = opts.unitPrice != null ? Number(opts.unitPrice) : null;
    var prices = opts.prices || null;
    var qty = clampQty(opts.value || 1, max);
    var onChange = typeof opts.onChange === 'function' ? opts.onChange : function () {};
    var uid = 'yxo-sq-' + (++sheetCount);

    el.classList.add('yxo');
    el.innerHTML =
      '<div class="yxo-squad">' +
        '<label class="yxo-label" for="' + uid + '">Copies</label>' +
        '<p class="yxo-hint">' + (prices ? esc(nb('One for every player, or one for each of the family. Extra framed copies of the same XI are ' + money(prices.extra) + ' each.'))
          : esc(nb('Ordering for the whole team? Get a framed copy for every player. Each copy is the same design.'))) + '</p>' +
        '<div class="yxo-qty">' +
          '<button type="button" class="yxo-step" data-d="-1" aria-label="One fewer copy">−</button>' +
          '<input class="yxo-num" id="' + uid + '" type="number" inputmode="numeric" min="1" max="' + max + '" value="' + qty + '" aria-describedby="' + uid + '-sum">' +
          '<button type="button" class="yxo-step" data-d="1" aria-label="One more copy">+</button>' +
        '</div>' +
        '<div class="yxo-quick" role="group" aria-label="Quick picks">' +
          CONFIG.QUICK.filter(function (n) { return n <= max; }).map(function (n) {
            return '<button type="button" class="yxo-chip" data-q="' + n + '">' + (n === 1 ? 'Just one' : n + ' copies') + '</button>';
          }).join('') +
        '</div>' +
        '<ul class="yxo-tiers" aria-label="Squad pricing"></ul>' +
        '<div class="yxo-total" id="' + uid + '-sum" aria-live="polite"></div>' +
      '</div>';

    var input = el.querySelector('.yxo-num');
    var tierList = el.querySelector('.yxo-tiers');
    var totalBox = el.querySelector('.yxo-total');

    function paint() {
      var p = squadPrice(qty, unitPrice, tiers);
      input.value = qty;
      el.querySelector('[data-d="-1"]').disabled = qty <= 1;
      el.querySelector('[data-d="1"]').disabled = qty >= max;
      el.querySelectorAll('.yxo-chip').forEach(function (c) { c.setAttribute('aria-pressed', String(+c.dataset.q === qty)); });

      if (prices) {
        tierList.hidden = true;
        var extra = qty - 1, tot = prices.first + extra * prices.extra;
        var ln = '<div class="yxo-tl"><span>First copy</span><span>' + esc(money(prices.first)) + '</span></div>';
        if (extra > 0) ln += '<div class="yxo-tl"><span>' + extra + ' extra ' + (extra === 1 ? 'copy' : 'copies') + ' × ' + esc(money(prices.extra)) + '</span><span>' + esc(money(extra * prices.extra)) + '</span></div>';
        ln += '<div class="yxo-tl yxo-grand"><span>Total</span><b>' + esc(money(tot)) + '</b></div>';
        ln += '<p class="yxo-each">' + esc(nb('Same frame and size for every copy. Free UK delivery on every copy.')) + '</p>';
        totalBox.innerHTML = ln; API.squadQty = qty; return;
      }
      tierList.hidden = false;
      tierList.innerHTML = tiers.map(function (t, i) {
        var on = i === p.tierIndex;
        var right = t.pct === 0 ? 'Full price' : pctText(t.pct) + ' off every copy';
        return '<li class="yxo-tier' + (on ? ' is-on' : '') + '"' + (on ? ' aria-current="true"' : '') + '>' +
          '<span>' + esc(tierLabel(t, i, tiers)) + '</span><span>' + esc(right) + '</span></li>';
      }).join('');

      var lines = '';
      lines += '<div class="yxo-tl"><span>' + qty + ' × ' + esc(money(unitPrice)) + '</span><span>' + esc(p.subtotal != null ? money(p.subtotal) : CONFIG.PRICE_PLACEHOLDER) + '</span></div>';
      if (p.pct !== 0) {
        lines += '<div class="yxo-tl yxo-save"><span>Squad saving, ' + esc(pctText(p.pct)) + ' off</span><span>' +
          esc(p.saving != null ? '−' + money(p.saving) : '−' + CONFIG.PRICE_PLACEHOLDER) + '</span></div>';
      }
      lines += '<div class="yxo-tl yxo-grand"><span>Total</span><b>' + esc(p.total != null ? money(p.total) : CONFIG.PRICE_PLACEHOLDER) + '</b></div>';
      if (qty > 1) lines += '<p class="yxo-each">' + esc(nb((p.each != null ? money(p.each) : CONFIG.PRICE_PLACEHOLDER) + ' a copy. Free UK delivery on every copy.')) + '</p>';
      if (p.next && p.next.tier.pct !== 0) {
        lines += '<p class="yxo-nudge">' + esc(nb('Add ' + p.next.more + ' more ' + (p.next.more === 1 ? 'copy' : 'copies') + ' and every copy is ' + pctText(p.next.tier.pct) + ' off.')) + '</p>';
      }
      totalBox.innerHTML = lines;
      API.squadQty = qty;
    }
    function set(q, fire) {
      var n = clampQty(q, max);
      var changed = n !== qty; qty = n; paint();
      if (changed && fire !== false) onChange(qty);
    }

    el.addEventListener('click', function (e) {
      var s = e.target.closest('.yxo-step'); if (s) { set(qty + Number(s.dataset.d)); return; }
      var c = e.target.closest('.yxo-chip'); if (c) set(+c.dataset.q);
    });
    input.addEventListener('change', function () { set(input.value); });
    input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); set(input.value); } });
    paint();

    return {
      update: function (q) { set(q, false); },
      setUnitPrice: function (p) { unitPrice = p != null ? Number(p) : null; paint(); },
      setPrices: function (p) { prices = p || null; var h = el.querySelector('.yxo-hint'); if (h && prices) h.textContent = nb('One for every player, or one for each of the family. Extra framed copies of the same XI are ' + money(prices.extra) + ' each.'); paint(); },
      getValue: function () { return qty; },
      destroy: function () { el.innerHTML = ''; el.classList.remove('yxo'); API.squadQty = 1; }
    };
  }

  API.mountGiftCardPrompt = mountGiftCardPrompt;
  API.mountGiftCardNote = mountGiftCardNote;
  API.mountSquadCopies = mountSquadCopies;
  API.squadPrice = squadPrice;
  API.money = money;
  root.YXIOffers = API;
})(typeof window !== 'undefined' ? window : this);
